import { Divider, Switch, Badge, Card, Row, Col, Form } from 'antd'
import { FormInstance } from 'antd/es/form/Form'
import React, { useEffect, useState } from 'react'
import { Tenant } from '../../../models/tenant'
import { GetTaxConfigurationsByLocation, TaxConfigurationResponse } from '../../../services/data-provider/tax'
import { theme } from '../../../theme'
import { useFormatMessage } from '../../../helpers/intl'

interface ApplicableTaxRulesSectionProps {
  tenant: Tenant
  form: FormInstance
}

export const ApplicableTaxRulesSection: React.FC<ApplicableTaxRulesSectionProps> = ({ tenant, form }) => {
  const [taxConfigurations, setTaxConfigurations] = useState<TaxConfigurationResponse[]>([])
  const [enabledTaxRules, setEnabledTaxRules] = useState<number[]>([])

  const applicableTaxRulesText = useFormatMessage(
    'pricing.applicableTaxRules',
    'Applicable Tax Rules'
  )

  useEffect(() => {
    const fetchTaxConfigurations = async () => {
      if (!tenant?.location) return

      const { province, postalCode } = tenant.location
      
      // Extract state code from province (could be "CA - California" or just "CA")
      let stateCode: string | undefined
      if (province) {
        if (province.includes(' - ')) {
          stateCode = province.split(' - ')[0].trim()
        } else {
          stateCode = province.trim()
        }
      }

      // Only query if we have at least state or zip code
      if (!stateCode && !postalCode) return

      try {
        const taxRules = await GetTaxConfigurationsByLocation(
          stateCode,
          postalCode,
          'active'
        )
        setTaxConfigurations(taxRules)
        
        // Initialize enabled tax rules from form - check both initial value and current value
        const formTaxRules = form.getFieldValue('tax_rules') || []
        setEnabledTaxRules(formTaxRules)
        
        // Also set the form field if it's not already set (for edit mode)
        if (formTaxRules.length === 0 && form.getFieldValue('tax_rules') === undefined) {
          form.setFieldsValue({ tax_rules: [] })
        }
      } catch (err) {
        console.error('Error fetching tax configurations:', err)
        setTaxConfigurations([])
      }
    }

    fetchTaxConfigurations()
  }, [tenant, form])

  // Sync enabledTaxRules with form value when form is initialized (e.g., when editing existing pricing schema)
  useEffect(() => {
    const formTaxRules = form.getFieldValue('tax_rules')
    if (formTaxRules && Array.isArray(formTaxRules) && JSON.stringify(formTaxRules) !== JSON.stringify(enabledTaxRules)) {
      setEnabledTaxRules(formTaxRules)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form])

  const formatRate = (taxConfig: TaxConfigurationResponse): React.ReactNode => {
    if (taxConfig.taxRateKwh && parseFloat(taxConfig.taxRateKwh) > 0) {
      return (
        <>
          <br />
          ${parseFloat(taxConfig.taxRateKwh).toFixed(6)} per kWh
        </>
      )
    }
    if (taxConfig.taxRatePercent && parseFloat(taxConfig.taxRatePercent) > 0) {
      return `${parseFloat(taxConfig.taxRatePercent)}% of ${taxConfig.isTaxPercentOnKwh ? 'cost per kWh' : 'total cost'}`
    }
    return 'N/A'
  }

  const formatLocation = (taxConfig: TaxConfigurationResponse): string => {
    if (taxConfig.states && taxConfig.states.length > 0) {
      const stateList = taxConfig.states.length === 1 
        ? `${taxConfig.states[0]}${taxConfig.zipCodes && taxConfig.zipCodes.length === 0 ? '' : ''}`
        : taxConfig.states.join(', ')
      if (taxConfig.zipCodes && taxConfig.zipCodes.length > 0) {
        return `${stateList} (${taxConfig.zipCodes.length} ZIP code${taxConfig.zipCodes.length > 1 ? 's' : ''})`
      }
      return stateList
    }
    if (taxConfig.zipCodes && taxConfig.zipCodes.length > 0) {
      return `${taxConfig.zipCodes.length} ZIP code${taxConfig.zipCodes.length > 1 ? 's' : ''}`
    }
    return 'Not specified'
  }

  const formatPowerThreshold = (taxConfig: TaxConfigurationResponse): string | null => {
    const min = taxConfig.powerThresholdMin ? parseFloat(taxConfig.powerThresholdMin) : null
    const max = taxConfig.powerThresholdMax ? parseFloat(taxConfig.powerThresholdMax) : null
    
    if (min !== null && max !== null) {
      return `${min} kW - ${max} kW`
    }
    if (min !== null) {
      return `≥ ${min} kW`
    }
    if (max !== null) {
      return `≤ ${max} kW`
    }
    return null
  }

  const handleTaxRuleToggle = (taxConfigId: number, enabled: boolean) => {
    setEnabledTaxRules((prev) => {
      let newTaxRules: number[]
      if (enabled) {
        // Add tax rule if not already present
        if (!prev.includes(taxConfigId)) {
          newTaxRules = [...prev, taxConfigId]
        } else {
          return prev
        }
      } else {
        // Remove tax rule
        newTaxRules = prev.filter((id: number) => id !== taxConfigId)
      }
      
      // Update form value
      form.setFieldsValue({
        tax_rules: newTaxRules
      })
      
      return newTaxRules
    })
  }

  if (taxConfigurations.length === 0) {
    return null
  }

  return (
    <>
      <Divider>
        <span className="paragraph-02-bold">
          {applicableTaxRulesText}
        </span>
      </Divider>
      {/* Hidden Form.Item to register tax_rules with the form */}
      <Form.Item name="tax_rules" hidden initialValue={[]}>
        <input type="hidden" />
      </Form.Item>
      <div style={{ marginTop: '-40px' }}>
      {taxConfigurations.map((taxConfig) => {
        const isEnabled = enabledTaxRules.includes(taxConfig.id)
        const powerThreshold = formatPowerThreshold(taxConfig)
        
        return (
          <Card
            key={taxConfig.id}
            style={{
              marginBottom: '8px',
              border: `1px solid ${theme.colors.grey3}`,
              borderRadius: '16px',
              width: '50%',
            }}
            bodyStyle={{ padding: '16px', paddingBottom: '12px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, marginRight: '8px' }}>
                    {taxConfig.taxName}
                  </span>
                  <Badge
                    status={taxConfig.status === 'active' ? 'success' : 'default'}
                    text={taxConfig.status === 'active' ? 'Active' : taxConfig.status}
                  />
                </div>
              </div>
              <div style={{ marginLeft: '16px' }}>
                <Switch
                  checked={isEnabled}
                  onChange={(checked) => handleTaxRuleToggle(taxConfig.id, checked)}
                  checkedChildren="Enabled"
                  unCheckedChildren="Disabled"
                />
              </div>
            </div>
            
            <Row gutter={[12, 6]} style={{ fontSize: '14px', fontFamily: theme.fontFamily.regularFontFamily, alignItems: 'flex-start' }}>
              <Col span={6}>
                <div style={{ color: theme.colors.grey7, marginBottom: '6px', lineHeight: '1.4', fontSize: '14px', fontFamily: theme.fontFamily.regularFontFamily }}>
                  <strong style={{ color: theme.colors.grey9, fontWeight: 400 }}>Rate:</strong> {formatRate(taxConfig)}
                </div>
              </Col>
              <Col span={6}>
                <div style={{ color: theme.colors.grey7, marginBottom: '6px', lineHeight: '1.4', fontSize: '14px', fontFamily: theme.fontFamily.regularFontFamily }}>
                  <strong style={{ color: theme.colors.grey9, fontWeight: 400 }}>Location: <br /> </strong> {formatLocation(taxConfig)}
                </div>
              </Col>
              <Col span={6}>
                <div style={{ color: theme.colors.grey7, marginBottom: '6px', lineHeight: '1.4', fontSize: '14px', fontFamily: theme.fontFamily.regularFontFamily }}>
                  <strong style={{ color: theme.colors.grey9, fontWeight: 400 }}>Free Sessions:</strong> {taxConfig.isFreeSessionExempt ? 'Exempt' : 'Taxed'}
                </div>
              </Col>
              {powerThreshold && (
                <Col span={6}>
                  <div style={{ color: theme.colors.grey7, marginBottom: '6px', lineHeight: '1.4', fontSize: '14px', fontFamily: theme.fontFamily.regularFontFamily }}>
                    <strong style={{ color: theme.colors.grey9, fontWeight: 400 }}>Power Threshold: <br /> </strong> {powerThreshold}
                  </div>
                </Col>
              )}
              {taxConfig.taxDescription && (
                <Col span={24}>
                  <div style={{ color: theme.colors.grey7, marginBottom: '6px', lineHeight: '1.4', fontSize: '14px', fontFamily: theme.fontFamily.regularFontFamily }}>
                    <strong style={{ color: theme.colors.grey9, fontWeight: 400 }}>Tax Description:</strong> {taxConfig.taxDescription}
                  </div>
                </Col>
              )}
              
              {taxConfig.taxAuthorityContact && (
                <Col span={6}>
                  <div style={{ color: theme.colors.grey7, marginBottom: '6px', lineHeight: '1.4' }}>
                    <strong style={{ color: theme.colors.grey9 }}>Tax Authority:</strong> {taxConfig.taxAuthorityContact}
                  </div>
                </Col>
              )}
              
              {taxConfig.regulatoryReference && (
                <Col span={6}>
                  <div style={{ color: theme.colors.grey7, marginBottom: '6px', lineHeight: '1.4' }}>
                    <strong style={{ color: theme.colors.grey9 }}>Regulatory Ref:</strong> {taxConfig.regulatoryReference}
                  </div>
                </Col>
              )}
            </Row>
          </Card>
        )
      })}
      </div>
    </>
  )
}

